<template>
  <div class="action-needed">
    <div class="action-needed__hint">
      Pick the statuses that count toward the "Action Needed" chip on the
      {{ scope === "pa" ? "Prior Auth" : "Intake" }} table. Changes apply to everyone on this company.
    </div>

    <div class="action-needed__list">
      <div v-if="loading" class="action-needed__empty">Loading…</div>
      <template v-else>
        <label v-for="opt in options" :key="opt" class="action-needed__row">
          <v-switch
            :model-value="selectedSet.has(opt)"
            density="compact"
            hide-details
            color="primary"
            class="action-needed__switch"
            @update:model-value="toggle(opt, $event)" />
          <span class="action-needed__row-label">{{ labelFor(opt) }}</span>
        </label>
      </template>
    </div>

    <div class="action-needed__save-bar">
      <v-btn variant="text" size="small" :disabled="saving" @click="resetToDefaults">Reset to Defaults</v-btn>
      <transition name="action-needed-fade">
        <span v-if="error" class="action-needed__msg action-needed__msg--error">{{ error }}</span>
        <span v-else-if="successMsg" class="action-needed__msg action-needed__msg--success">{{ successMsg }}</span>
      </transition>
      <v-spacer />
      <v-btn variant="flat" size="small" color="primary" :loading="saving" :disabled="!hasChanges" @click="save">
        Save
      </v-btn>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { settingsService } from "@/services/settingsService";
import { useRequirementsStore } from "@/stores";
import { CLIENT_CONFIG_SECTION, toBackendScope, isValidScope } from "@/constants/scope";
import { useSettingsSaveFlow } from "@/composables/useSettingsSaveFlow";

// Matches the existing statusOptions tables in PA / Intake pages so the labels
// users see in Settings match the chip/dropdown labels everywhere else.
const LABELS = {
  pa: {
    INCOMPLETE: "Incomplete",
    REQUIRES_REVIEW: "Requires Review",
    DEMOGRAPHICS_REVIEW: "Demographics Review",
    CLINICAL_REVIEW: "Clinical Review",
    INITIATED: "Initiated",
    CREATED: "Fetching Questions",
    PROCESSING: "Processing",
    SENT_TO_PLAN: "Sent to Plan",
    SENT_TO_PRESCRIBER: "Sent to Prescriber",
    APPROVED: "Approved",
    DENIED: "Denied",
    PA_NOT_REQUIRED: "PA Not Required",
    NOT_SUPPORTED: "Not Supported",
    NO_PAYOR_RESPONSE_RECEIVED: "No Payor Response",
    REQUIRES_MORE_INFORMATION: "Requires More Info",
    RESPONSE_RECEIVED: "Response Received",
  },
  intake: {
    INTAKE: "Document Review",
    QUALIFY_PATIENT: "Confirm Order Details",
    CLINICAL_REVIEW: "Clinical Review",
    PRE_PROCESSING: "Pre-Processing",
    OCR_PROCESSED: "OCR Complete",
    EHR_PROCESSING: "EHR Processing",
    SENT_TO_EHR: "Sent to EHR",
    INTAKE_COMPLETE: "Intake Complete",
    PRIOR_AUTHORIZATION: "Ready for PA",
    APPROVED: "Approved",
    DENIAL_FOLLOWUP: "Denial Follow-up",
  },
};

const props = defineProps({
  scope: {
    type: String,
    required: true,
    validator: isValidScope,
  },
});

const requirementsStore = useRequirementsStore();
const { saving, error, successMsg, run: runSave } = useSettingsSaveFlow();

const loading = ref(false);

// Cached response from GET /action-needed. Keyed by "intake" / "priorAuth"
// (backend response shape). Holds { selected: [...], options: [...] }.
const data = ref({ intake: { selected: [], options: [] }, priorAuth: { selected: [], options: [] } });

// Snapshot of server-persisted selection so we know when the UI is dirty.
const initial = ref({ intake: [], pa: [] });
// Local editable selection.
const selected = ref({ intake: [], pa: [] });

const options = computed(() => {
  const key = CLIENT_CONFIG_SECTION[props.scope];
  return data.value[key]?.options || [];
});

const selectedSet = computed(() => new Set(selected.value[props.scope]));

const hasChanges = computed(() => {
  const cur = [...selected.value[props.scope]].sort();
  const base = [...initial.value[props.scope]].sort();
  return JSON.stringify(cur) !== JSON.stringify(base);
});

function labelFor(key) {
  return LABELS[props.scope]?.[key] || key;
}

function toggle(statusKey, enabled) {
  const cur = new Set(selected.value[props.scope]);
  if (enabled) cur.add(statusKey);
  else cur.delete(statusKey);
  // Preserve the option-catalog order so payload and UI stay aligned.
  const order = new Map(options.value.map((k, i) => [k, i]));
  selected.value = {
    ...selected.value,
    [props.scope]: [...cur].sort((a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0)),
  };
}

async function load() {
  loading.value = true;
  error.value = "";
  try {
    const res = await settingsService.getActionNeededStatuses();
    data.value = res || data.value;
    const intakeSelected = [...(res?.intake?.selected || [])];
    const paSelected = [...(res?.priorAuth?.selected || [])];
    initial.value = { intake: intakeSelected, pa: paSelected };
    selected.value = { intake: [...intakeSelected], pa: [...paSelected] };
  } catch {
    error.value = "Failed to load action-needed settings.";
  } finally {
    loading.value = false;
  }
}

async function save() {
  await runSave(async () => {
    const backendScope = toBackendScope(props.scope);
    const payload = selected.value[props.scope];
    const response = await settingsService.updateActionNeededStatuses(backendScope, payload);
    const savedList = Array.isArray(response?.statuses) ? response.statuses : payload;
    initial.value = { ...initial.value, [props.scope]: [...savedList] };
    selected.value = { ...selected.value, [props.scope]: [...savedList] };
    await requirementsStore.refreshCurrentUserClientConfig();
  });
}

async function resetToDefaults() {
  await runSave(
    async () => {
      await settingsService.resetActionNeededStatuses(toBackendScope(props.scope));
      await requirementsStore.refreshCurrentUserClientConfig();
      await load();
    },
    { successText: "Reset to defaults." }
  );
}

watch(
  () => props.scope,
  () => {
    error.value = "";
    successMsg.value = "";
  }
);

load();
</script>

<style lang="scss" scoped>
@import "@/styles/_variables.scss";

.action-needed {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.action-needed__hint {
  font-size: 12.5px;
  color: $lh_color_blue_gray;
  line-height: 1.45;
}

.action-needed__list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.action-needed__empty {
  font-size: 13px;
  color: $lh_color_blue_gray;
  padding: 12px 0;
}

.action-needed__row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color $lh_transition_fast;

  &:hover {
    background: $lh_color_snowy_white;
  }
}

.action-needed__switch {
  flex: 0 0 auto;
}

.action-needed__row-label {
  font-family: $lh_font_family_dm_sans;
  font-size: $lh_body3_1_size;
  color: $lh_color_navy_dark;
}

.action-needed__save-bar {
  display: flex;
  align-items: center;
  padding: 12px 0 0;
  border-top: 1px solid $lh_dash_border;
  position: sticky;
  bottom: 0;
  background: white;
  z-index: 1;
}

.action-needed__msg {
  font-family: $lh_font_family_dm_sans;
  font-size: 12px;
  margin-left: 12px;
}

.action-needed__msg--error {
  color: $lh_color_red;
}

.action-needed__msg--success {
  color: $lh_color_green;
}

.action-needed-fade-enter-active,
.action-needed-fade-leave-active {
  transition: opacity 160ms;
}

.action-needed-fade-enter-from,
.action-needed-fade-leave-to {
  opacity: 0;
}
</style>
